import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactPlaceholder from 'react-placeholder';
import {TextRow, RoundShape} from 'react-placeholder/lib/placeholders';
import MomentCore from 'moment';
import Moment from "react-moment";

import {
	makeStyles,
	useMediaQuery,
	useTheme,
	Typography,
	Grid,
	Button
} from '@material-ui/core';

import AuditTrailIcon from "./AuditTrailIcon";
import Constants from "../utils/Constants";
import Timestamp from './Timestamp.js';

// this needs to match AuditTrailIcon size, which can be passed into that component but 28 is the default
const iconSize = 28;
const rowHeight = 85;

const styles = makeStyles(theme => ({
	dateHeading: {
		fontSize: "0.9rem"
	},

	row: {
		height: rowHeight,
		maxHeight: rowHeight,
		position: "relative",
		alignItems: "center",
		padding: theme.spacing(1.5),
		marginBottom: theme.spacing(1),
		borderRadius: theme.spacing(0.625),
		backgroundColor: theme.palette.primary.grey
	},

	iconCol: {
		paddingLeft: theme.spacing(1)
	},

	textCol: {
		paddingLeft: theme.spacing(2),
		width: `calc(100% - ${2 * theme.spacing(3)}px)`,

		// TODO: migrate to useEllipsis() hook from our custom hooks
		// all for multi-line ellipsis
		"& > p:first-child": {
			position: "relative",
			overflow: "hidden",
			maxHeight: "36%",

			[theme.breakpoints.up('lg')]: {
				maxHeight: "63%",
			},

			/* fix problem when last visible word doesn't adjoin right side  */
			//textAlign: "justify",
			/* place for '...' */
			marginRight: "-1em",
			paddingRight: "1.5em",
			wordBreak: "break-word",

			"&:before": {
				content: "'...'",
				position: "absolute",
				/* set position to right bottom corner of block */
				right: 8,
				bottom: 0
			},

			/* hide ... if we have text, which is less than or equal to max lines */
			"&:after": {
				/* points in the end */
				content: "''",
				/* absolute position */
				position: "absolute",
				/* set position to right bottom corner of text */
				right: 8,
				/* set width and height */
				width: "1em",
				height: "1em",
				marginTop: "0.2em",
				/* bg color = bg color under block */
				background: theme.palette.primary.grey
			}
		}
	},

	viewAllUserActivity: {
		marginLeft: theme.spacing(-1.5)
	}
}));

const placeholderColor = Constants.COLORS.PLACEHOLDER_BG;
export const placeHolder = (
	<div style={{display: "flex", padding: 24, paddingLeft: 12}}>
		<div style={{marginRight: 10}}>
			<RoundShape color={placeholderColor} style={{height: iconSize, width: iconSize}} />
		</div>
		<div style={{flex: 1}}>
			<TextRow style={{width: "60%", marginTop: 0}} color={placeholderColor} />
			<TextRow style={{width: "40%"}} color={placeholderColor} />
		</div>
	</div>
);

function ActivityAuditTrail(props) {
	const [allActivity, setAllActivity] = useState(false);

	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("xs"));

	const getText = (obj) => {
		let text,
			prefix,
			whoName = obj.whoName,
			withName = obj.withName,
			useVerbose = props.useVerbose,
			activityText = "this";

		if (useVerbose) {
			activityText = `${obj.action === Constants.ACTIVITY.AUDIT_TRAIL.COMMENT.ID ? "on" : ""}
				${Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.ID === obj.category ? "a discussion" : "an alert"}`;
		}

		if (isXs) {
			whoName = obj.whoName ? obj.whoName.split(" ") : [];
			withName = withName ? withName.split(" ") : [];

			whoName = whoName.length > 1 ? whoName[0].charAt(0) + ". " + whoName[1] : obj.whoName;
			if (withName.length) {
				withName = withName.length > 1 ? withName[0].charAt(0) + ". " + withName[1] : obj.withName;
			}
		}

		if (obj.action === Constants.ACTIVITY.AUDIT_TRAIL.STATUS_CHANGE.ID) {
			prefix = <span>{whoName} changed the status to <i>{Constants.ACTIVITY.AUDIT_TRAIL.STATUS_CHANGE.getText(obj.status)}</i></span>;
		} else if (obj.action === Constants.ACTIVITY.AUDIT_TRAIL.CARE_STATUS_CHANGE.ID) {
			prefix = <span>{whoName} {Constants.ACTIVITY.AUDIT_TRAIL.CARE_STATUS_CHANGE.getText(obj.status)}</span>;
		} else if (obj.action === Constants.ACTIVITY.AUDIT_TRAIL.REPORT_TO_BTA.ID) {
			prefix = <span>{whoName} <i>{Constants.ACTIVITY.AUDIT_TRAIL.REPORT_TO_BTA.getText()}</i> this to BCM</span>;
		} else {
			prefix = <span>{whoName} <i>{obj.action}{obj.action === Constants.ACTIVITY.AUDIT_TRAIL.COMMENT.ID ? "ed" : "d"}</i></span>;
		}

		switch (obj.action) {
			case Constants.ACTIVITY.AUDIT_TRAIL.SHARE.ID:
				text = <span>{prefix} {!isXs || useVerbose ? activityText : ""} with {withName}</span>;
				break;

			case Constants.ACTIVITY.AUDIT_TRAIL.CARE_STATUS_CHANGE.ID:
			case Constants.ACTIVITY.AUDIT_TRAIL.STATUS_CHANGE.ID:
			case Constants.ACTIVITY.AUDIT_TRAIL.REPORT_TO_BTA.ID:
				text = <span>{prefix}</span>;
				break;

			default:
				text = <span>{prefix} {activityText}</span>;
		}

		return <Typography>{text}</Typography>;
	};

	let currDate,
		classes = styles(),
		{fidoError, userActivity, maxActivitiesShown, className, category} = props,
		ready = userActivity != null,
		isSameDay = (momentDate1, momentDate2) => {
			if (momentDate1 == null || momentDate2 == null) {
				return false;
			}
			return momentDate1.isSame(momentDate2, "day");
		};

	return (
		<ReactPlaceholder customPlaceholder={placeHolder} ready={ready} showLoadingAnimation={!fidoError}>
			<div className={`${className}`}>
				{ready &&  userActivity && userActivity.length ?
					userActivity.map((activity, idx) => {
						if (maxActivitiesShown != null && idx >= maxActivitiesShown && !allActivity) {
							if (idx > maxActivitiesShown) {
								return null;
							}

							return (
								<Button key={`key-${activity.date}-${idx}`}
									color="primary"
									onClick={() => setAllActivity(true)}
									className={classes.viewAllUserActivity}>View all user activity</Button>
							);
						}

						let dateHeading,
							activityDate = new MomentCore.utc(activity.date).local();

						if (!isSameDay(activityDate, currDate) || currDate == null) {
							currDate = activityDate;
							dateHeading = <Typography color="textPrimary" className={classes.dateHeading}>
								<Moment format={"MMMM Do"}>{currDate}</Moment>
							</Typography>;
						}

						return <div key={`key-${activity.date}-${idx}`}>
							{dateHeading}
							<Grid container justify="flex-start" className={classes.row}>
								<Grid item className={classes.iconCol}>
									<AuditTrailIcon type={activity.type} />
								</Grid>
								<Grid item className={classes.textCol}>
									{getText({
										category: category,
										status: activity.status,
										whoName: activity.user && 'name' in activity.user && activity.user.name || "A removed user",
										action: activity.type,
										withName: activity.recipient ? activity.recipient.name || activity.recipient.phone || activity.recipient.email : null
									})}
									<Timestamp time={activity.date} />
								</Grid>
							</Grid>
						</div>;
					})
					:
					<Typography color="textSecondary" className={classes.noData}>
						No recent actions
					</Typography>
				}
			</div>
		</ReactPlaceholder>
	);
}

ActivityAuditTrail.propTypes = {
	className: PropTypes.string,
	userActivity: PropTypes.array,
	maxActivitiesShown: PropTypes.number,
	fidoError: PropTypes.bool,
	useVerbose: PropTypes.bool,
	category: PropTypes.number
};

export default ActivityAuditTrail;
